import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  TranscriptExtractionData,
  Course,
  FieldWithBbox,
  RequiredFields,
  InstitutionKeys
} from '../../models/transcriptExtractionData.model';
import { REVIEW_CONFIDENCE_THRESHOLD, TranscriptType, ValidateElementType } from '../../constants';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

export enum ValidationErrorType {
  missingRequiredFields = 'missingRequiredFields',
  specialCharactersInName = 'specialCharactersInName'
}

export interface ValidationResult {
  isValid: boolean;
  missingSections: string[];
  errorType?: ValidationErrorType;
}

export interface TranscriptValidationResult extends ValidationResult {
  transcriptId: string;
  studentName: string;
}

@Injectable({
  providedIn: 'root'
})
export class ItpExtractionValidationService {
  private featureFlagService = inject(FeatureFlagService);

  private readonly fieldMap = new Map([
    ['firstname', 'FirstName'],
    ['middlename', 'MiddleName'],
    ['lastname', 'LastName'],
    ['dateofbirth', 'DateOfBirth'],
    ['email', 'Email'],
    ['last4ssn', 'Last4SSN'],
    ['term', 'Term'],
    ['startdate', 'StartDate'],
    ['enddate', 'EndDate'],
    ['subject', 'Subject'],
    ['coursenumber', 'CourseNumber'],
    ['coursetitle', 'CourseTitle'],
    ['creditsearned', 'CreditsEarned'],
    ['grade', 'Grade'],
    ['gradepoints', 'GradePoints'],
    ['institution', 'Institution'],
    ['institutionid', 'InstitutionId'],
    ['institutionname', 'Name'],
    ['totalcreditsearned', 'TotalCreditsEarned'],
    ['cumulativegpa', 'CumulativeGPA']
  ]);

  private readonly reverseFieldMap = new Map(
    Array.from(this.fieldMap.entries()).map(([k, v]) => [v.toLowerCase(), k])
  );

  colleagueSpecialCharFlag = toSignal(
    this.featureFlagService.getFlag('license-itp-colleague-send-to-sis', false)
  );

  /**
   * Validates the transcript data against the required fields and returns a ValidationResult indicating which sections are missing
   * @param transcriptData The extracted transcript data to validate
   * @param requiredFields The required fields configuration to validate against
   */
  validateRequiredFields(
    transcriptData: TranscriptExtractionData | null,
    requiredFields: RequiredFields,
    transcriptType: TranscriptType
  ): ValidationResult {
    const missingSections: string[] = [];

    if (this.hasStudentMissingFields(transcriptData, requiredFields.student)) {
      missingSections.push('Student');
    }

    if (this.hasInstitutionMissingFields(transcriptData, requiredFields.institution)) {
      missingSections.push('Institution');
    }

    if (
      transcriptType === TranscriptType.College &&
      this.hasCoursesMissingFields(transcriptData, requiredFields.course)
    ) {
      missingSections.push('Courses');
    }

    return { isValid: missingSections.length === 0, missingSections };
  }

  /**
   * Validates multiple transcripts' extracted data against the required fields and returns an array of TranscriptValidationResult with the validation outcomes and missing sections for each transcript
   * @param transcripts An array of objects containing the extracted transcript data and corresponding transcript ID to validate
   * @param requiredFields The required fields configuration to validate against
   */
  validateTranscripts(
    transcripts: {
      data: TranscriptExtractionData;
      transcriptId: string;
      transcriptType: TranscriptType;
    }[],
    requiredFields: Map<TranscriptType, RequiredFields> | null
  ): TranscriptValidationResult[] {
    return transcripts.map(t => {
      const firstName = String(t.data?.Student?.FirstName?.value || '');
      const middleName = String(t.data?.Student?.MiddleName?.value || '');
      const lastName = String(t.data?.Student?.LastName?.value || '');
      const studentName = `${firstName} ${lastName}`.trim() || 'Unknown';

      if (this.colleagueSpecialCharFlag()) {
        const specialCharPattern = /[^a-zA-Z0-9\s\-'\.]/;
        if (
          studentName !== 'Unknown' &&
          [firstName, middleName, lastName].some(n => specialCharPattern.test(n))
        ) {
          return {
            transcriptId: t.transcriptId,
            studentName,
            isValid: false,
            missingSections: [],
            errorType: ValidationErrorType.specialCharactersInName
          };
        }
      }

      const requiredFieldsForType = requiredFields?.get(t.transcriptType) ?? null;

      if (!requiredFieldsForType) {
        return { transcriptId: t.transcriptId, studentName, isValid: true, missingSections: [] };
      }

      const result = this.validateRequiredFields(t.data, requiredFieldsForType, t.transcriptType);

      return {
        transcriptId: t.transcriptId,
        studentName,
        isValid: result.isValid,
        missingSections: result.missingSections,
        errorType: result.isValid ? undefined : ValidationErrorType.missingRequiredFields
      };
    });
  }

  /**
   * Checks if the student section of the transcript data is missing any required fields
   * @param transcriptData The extracted transcript data to check
   * @param requiredStudentFields The list of required student fields to check against
   */
  private hasStudentMissingFields(
    transcriptData: TranscriptExtractionData | null,
    requiredStudentFields: string[]
  ): boolean {
    if (!requiredStudentFields?.length) return false;
    if (!transcriptData?.Student) return true;

    return requiredStudentFields.some(fieldName => {
      const fieldKey = this.getMappedFieldKey(fieldName);
      if (!fieldKey) return false;
      const fieldValue = transcriptData.Student[
        fieldKey as keyof typeof transcriptData.Student
      ] as FieldWithBbox;
      return !fieldValue?.value || String(fieldValue.value).trim() === '';
    });
  }

  /**
   * Checks if the institution section of the transcript data is missing any required fields
   * @param transcriptData The extracted transcript data to check
   * @param requiredInstitutionFields The list of required institution fields to check against
   */
  private hasInstitutionMissingFields(
    transcriptData: TranscriptExtractionData | null,
    requiredInstitutionFields: string[]
  ): boolean {
    if (!requiredInstitutionFields?.length) return false;
    if (!transcriptData?.Institutions?.length) return true;

    return requiredInstitutionFields.some(fieldName => {
      const fieldKey = this.getMappedFieldKey(fieldName);
      if (!fieldKey) return false;
      const fieldValue = transcriptData.Institutions[0][
        fieldKey as (typeof InstitutionKeys)[number]
      ] as FieldWithBbox;
      return !fieldValue?.value || String(fieldValue.value).trim() === '';
    });
  }

  /**
   * Checks if the courses section of the transcript data is missing any required fields
   * @param transcriptData The extracted transcript data to check
   * @param requiredCourseFields The list of required course fields to check against
   */
  private hasCoursesMissingFields(
    transcriptData: TranscriptExtractionData | null,
    requiredCourseFields: string[]
  ): boolean {
    if (!transcriptData?.Courses?.length) return true;
    if (!requiredCourseFields?.length) return false;

    return requiredCourseFields.some(fieldName => {
      const fieldKey = this.getMappedFieldKey(fieldName);
      if (!fieldKey) return false;
      return transcriptData.Courses.some(course => {
        const fieldValue = course[fieldKey as keyof Course] as FieldWithBbox;
        return !fieldValue?.value || String(fieldValue.value).trim() === '';
      });
    });
  }

  /**
   * Maps a given field name to its corresponding key in the transcript data model using a predefined mapping, returning null if no mapping exists
   * @param fieldName The name of the field to map
   */
  getMappedFieldKey(fieldName: string): string | null {
    return this.fieldMap.get(fieldName.toLowerCase()) || null;
  }

  /**
   * Determines whether to show a required field indicator or error based on the field type and value
   * @param fieldName The name of the field to check
   * @param type The type of validation element
   * @param fieldValue The value of the field
   * @param transcriptData The transcript data
   * @param requiredFields The required fields
   */
  showRequiredElement(
    fieldName: string,
    type: ValidateElementType,
    fieldValue: any,
    transcriptData: TranscriptExtractionData | null,
    requiredFields: RequiredFields
  ): boolean {
    if (!transcriptData) return false;

    if (type === ValidateElementType.Asterisk) {
      return (
        requiredFields.student.includes(fieldName.toLowerCase()) ||
        requiredFields.course.includes(fieldName.toLowerCase()) ||
        requiredFields.institution.includes(fieldName.toLowerCase())
      );
    }

    if (type === ValidateElementType.Text) {
      const lowerFieldName = fieldName.toLowerCase();
      const mappedKey = this.getMappedFieldKey(lowerFieldName as (typeof InstitutionKeys)[number]);
      if (!mappedKey) return false;

      if (requiredFields.student.includes(lowerFieldName)) {
        const value = transcriptData.Student?.[
          mappedKey as keyof typeof transcriptData.Student
        ] as FieldWithBbox;
        return String(value?.value || '').trim() === '';
      }

      if (requiredFields.institution.includes(lowerFieldName)) {
        const value = transcriptData.Institutions?.[0]?.[mappedKey] as FieldWithBbox;
        return String(value?.value || '').trim() === '';
      }
    }

    if (type === ValidateElementType.Icon) {
      return (
        (requiredFields.student.includes(fieldName.toLowerCase()) ||
          requiredFields.course.includes(fieldName.toLowerCase()) ||
          requiredFields.institution.includes(fieldName.toLowerCase())) &&
        (fieldValue === null || String(fieldValue).trim() === '')
      );
    }

    return false;
  }

  /**
   * Calculates the number of fields that need attention based on low confidence or missing values
   * @param transcriptData The transcript data to analyze
   * @param requiredFields The required fields configuration to check against
   * @param confidenceThreshold The confidence score threshold below which a field is considered to need attention (default is 80)
   */
  calculateFieldsNeedingAttention(
    transcriptData: TranscriptExtractionData | null,
    requiredFields: RequiredFields,
    confidenceThreshold = REVIEW_CONFIDENCE_THRESHOLD
  ): number {
    if (!transcriptData) return 0;

    let count = 0;

    const checkField = (
      field: FieldWithBbox | undefined,
      fieldName: string,
      section: keyof RequiredFields
    ) => {
      if (!field) return;
      if (field.reviewed) return;
      const isRequired = requiredFields[section]?.includes(fieldName.toLowerCase()) || false;
      const isEmpty = !field.value || field.value === '';
      const isLowConfidence = field.confidence != null && field.confidence < confidenceThreshold;
      if ((isRequired && isEmpty) || isLowConfidence) count++;
    };

    const checkSection = (obj: Record<string, any>, section: keyof RequiredFields) => {
      if (!obj) return;
      Object.entries(obj).forEach(([key, field]) => {
        if (field && typeof field === 'object' && 'confidence' in field) {
          const fieldName = this.reverseFieldMap.get(key.toLowerCase()) || key;
          checkField(field as FieldWithBbox, fieldName, section);
        }
      });
    };

    checkSection(transcriptData.Student, 'student');
    transcriptData.Institutions?.forEach((inst: Record<string, FieldWithBbox>) =>
      checkSection(inst, 'institution')
    );
    transcriptData.Courses?.forEach(course => checkSection(course, 'course'));

    return count;
  }
}
